import { useMemo } from 'react';
import { BookingList } from '@/components/bookings/BookingList';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { useFilters } from '@/lib/filters';
import { formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { useBookings } from '@/lib/queries';

/*
 * Named bookings inside an expanded ad row (ad-level only). Filters the shared
 * range-wide /bookings response down to entries our attribution paired with
 * THIS ad (booking.ad.adId === entityId). When Meta's per-ad count and our
 * named count disagree, both are shown honestly with a caveat explaining why
 * (bookingNameMatch in the methodology registry). Renders nothing when both
 * sides are zero — most ads have no bookings, and silence beats noise. Rows
 * render via the shared BookingList (same look as the Overview drill-downs).
 */

const MESSAGES = {
  nb: {
    headerEqual: (n: string) => `Bookinger (${n})`,
    headerDiff: (meta: string, named: string) =>
      `Meta: ${meta} · navngitt av vår sporing: ${named}`,
    noneNamed: 'Ingen navngitte bookinger matchet denne annonsen i perioden.',
  },
  en: {
    headerEqual: (n: string) => `Bookings (${n})`,
    headerDiff: (meta: string, named: string) => `Meta: ${meta} · named by our tracking: ${named}`,
    noneNamed: 'No named bookings matched this ad in this range.',
  },
};

export function AdBookings({ adId, metaBookings }: { adId: string; metaBookings: number }) {
  const m = useViewMessages(MESSAGES);
  const { from, to } = useFilters();
  const q = useBookings(from, to);

  const matched = useMemo(
    () => (q.data?.bookings ?? []).filter((b) => b.ad?.adId === adId),
    [q.data, adId],
  );

  // Quiet degradation: while loading or on error we can't name anyone — the
  // engagement panel above still carries the row, so render nothing.
  if (!q.data) return null;
  if (metaBookings === 0 && matched.length === 0) return null;

  const differs = metaBookings !== matched.length;

  return (
    <div className="mt-3 border-t border-border/60 pt-3">
      <p className="flex flex-wrap items-center gap-1.5 text-label font-medium text-fg">
        {differs
          ? m.headerDiff(formatMetric(metaBookings, 'int'), formatMetric(matched.length, 'int'))
          : m.headerEqual(formatMetric(matched.length, 'int'))}
        {differs ? <CaveatRef id="bookingNameMatch" /> : null}
      </p>
      <div className="mt-1.5">
        <BookingList bookings={matched} emptyText={m.noneNamed} />
      </div>
    </div>
  );
}
